import type { SupabaseClient } from "@supabase/supabase-js";
import { ArrowLeft, Loader2, Mail, ShieldCheck } from "lucide-react";
import { useState } from "react";

/**
 * Email plus a 6-digit code. Deliberately not a magic link: the same flow then works
 * unchanged inside the Capacitor builds, with no App Links or Universal Links to set up.
 */
export function SignIn({
  supabase,
  onSignedIn,
}: {
  supabase: SupabaseClient;
  onSignedIn?: () => void;
}) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function sendCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const { error: sendError } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { shouldCreateUser: true },
    });

    setBusy(false);
    if (sendError) {
      setError(sendError.message);
      return;
    }
    setStep("code");
  }

  async function verify(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const { error: verifyError } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: code.trim(),
      type: "email",
    });

    setBusy(false);
    if (verifyError) {
      setError(verifyError.message);
      return;
    }
    onSignedIn?.();
  }

  return (
    <section className="rounded-2xl border border-zinc-800/80 bg-zinc-900/40 p-4 backdrop-blur-sm sm:p-5">
      <div className="mb-3 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-emerald-300" />
        <h2 className="text-sm font-semibold text-zinc-100">
          {step === "email" ? "Sign in to generate" : "Check your email"}
        </h2>
      </div>

      {step === "email" ? (
        <form onSubmit={sendCode} className="space-y-3">
          <p className="text-[12.5px] leading-relaxed text-zinc-400">
            We&rsquo;ll email you a sign-in code. No password, and the same flow works in the
            mobile apps.
          </p>
          <label className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
              <Mail className="h-3.5 w-3.5" />
              Email
            </span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@company.com"
              className="w-full rounded-xl border border-zinc-800 bg-zinc-950/70 px-3.5 py-2.5 text-sm text-zinc-100 outline-none transition placeholder:text-zinc-600 focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/20"
            />
          </label>
          <button
            type="submit"
            disabled={busy || email.trim().length < 5}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-400 to-emerald-500 px-4 py-2.5 text-sm font-bold text-zinc-950 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Email me a code
          </button>
        </form>
      ) : (
        <form onSubmit={verify} className="space-y-3">
          <p className="text-[12.5px] leading-relaxed text-zinc-400">
            Enter the code sent to <span className="text-zinc-200">{email}</span>.
          </p>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            required
            value={code}
            // Supabase projects issue six or eight digits depending on configuration,
            // so accept the range rather than pinning one length.
            onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 10))}
            placeholder="000000"
            className="w-full rounded-xl border border-zinc-800 bg-zinc-950/70 px-3.5 py-2.5 text-center font-mono text-lg tracking-[0.4em] text-zinc-100 outline-none transition placeholder:text-zinc-700 focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/20"
          />
          <button
            type="submit"
            disabled={busy || code.length < 6}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-400 to-emerald-500 px-4 py-2.5 text-sm font-bold text-zinc-950 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Verify and continue
          </button>
          <button
            type="button"
            onClick={() => {
              setStep("email");
              setCode("");
              setError(null);
            }}
            className="flex w-full items-center justify-center gap-1.5 text-[11.5px] text-zinc-500 transition hover:text-zinc-300"
          >
            <ArrowLeft className="h-3 w-3" />
            Use a different email
          </button>
        </form>
      )}

      {error ? (
        <p className="mt-3 rounded-lg border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-[11.5px] text-rose-300">
          {error}
        </p>
      ) : null}
    </section>
  );
}
